import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, Select, Space, Table } from 'antd';
import TransactionModal, { useTransactionModal } from './TransactionModal';
import { useConnection } from '../../connection/connection';
import {
  AccountTransaction,
  useBusinessContext,
} from '../../context/BusinessContext';
import { useEventListener } from '../../common/EventContext';
import { Utils } from '../../common/Utils';
import classNames from 'classnames';
import { EditOutlined } from '@ant-design/icons';
import JournalModal, { useJournalModal } from './JournalModal';
import { Account } from '../../dto';

type Props = {};

export const TransactionsPage: FC<Props> = (props) => {
  const txModal = useTransactionModal();
  const journalModal = useJournalModal();
  const connection = useConnection();
  const business = useBusinessContext();
  const [siteId, setSiteId] = useState<string>('0');
  const [accountId, setAccountId] = useState<string>('0');
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [lastKey, setLastKey] = useState<string>();
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [totalBalance, setTotalBalance] = useState<number>(0);

  useEffect(() => {
    setLoading(true);
    connection
      .get(`transactions/${business.businessId}/activities`, {
        params: {
          site: siteId === '0' ? undefined : siteId,
          account: accountId === '0' ? undefined : accountId,
        },
      })
      .then((res) => {
        setTransactions(res.data.transactions);
        setLastKey(res.data.lastKey);
      })
      .finally(() => setLoading(false));
  }, [accountId, business.businessId, connection, siteId]);

  const getAccountBalance = useCallback(
    (account: Account): number => {
      return (
        (balances[account.accountId] ?? 0) *
        (account.accountCategory === 'Liabilities' ? -1 : 1)
      );
    },
    [balances],
  );
  const bus = useRef<any>();
  const accb = useRef<any>();
  const getAccountBalanceInBusinessCcy = useCallback(
    async (account: Account): Promise<number> => {
      console.log(
        'getAccountBalanceInBusinessCcy',
        business !== bus.current,
        accb.current !== getAccountBalance,
      );
      bus.current = business;
      accb.current = getAccountBalance;
      const amount = getAccountBalance(account);
      const rate = await business.getCurrencyRate(
        account.currency,
        business.currency,
      );

      return amount * (rate?.rate ?? 1);
    },
    [business, getAccountBalance],
  );

  const getTotalBalanceInBusinessCcy =
    useCallback(async (): Promise<number> => {
      const balances = await Promise.all(
        Object.values(business.accounts)
          .filter(
            (a) =>
              a.activity &&
              (!a.siteId || a.siteId === siteId || !siteId || siteId === '0'),
          )
          .map((v) => getAccountBalanceInBusinessCcy(v)),
      );
      return balances.reduce((a, b) => a + b, 0);
    }, [business.accounts, siteId, getAccountBalanceInBusinessCcy]);

  useEffect(() => {
    getTotalBalanceInBusinessCcy().then(setTotalBalance);
  }, [getTotalBalanceInBusinessCcy]);

  const loadMore = useCallback(() => {
    setLoading(true);
    const lastTx = transactions[transactions.length - 1];
    connection
      .get(`transactions/${business.businessId}/activities`, {
        params: {
          start: lastKey,
          site: siteId === '0' ? undefined : siteId,
          account: accountId === '0' ? undefined : accountId,
        },
      })
      .then((res) => {
        setTransactions((prevState) => [
          ...prevState,
          ...res.data.transactions,
        ]);
        setLastKey(res.data.lastKey);
      })
      .finally(() => setLoading(false));
  }, [
    accountId,
    business.businessId,
    connection,
    lastKey,
    siteId,
    transactions,
  ]);

  useEventListener(
    'ACTIVITIES',
    useCallback((event) => {
      const { action, data } = event;
      if (action === 'CREATE') {
        setTransactions((prevState) => {
          const txs = data as AccountTransaction[];
          if (txs.length === 0) {
            return prevState;
          }
          const txId = txs[0].txId;
          const ns = prevState.filter((a) => a.txId !== txId);
          return [...ns, ...data].sort((a, b) =>
            `${b.date}:${b.timestamp}`.localeCompare(
              `${a.date}:${a.timestamp}`,
            ),
          );
        });
      } else if (action === 'DELETE') {
        console.log('DELETED', data);
        setTransactions((prevState) =>
          prevState.filter((a) => {
            console.log(
              'COMPARE a.txId !== data',
              a.txId,
              data,
              a.txId !== data,
            );
            return a.txId !== data;
          }),
        );
      }
    }, []),
  );

  const columns: any = useMemo(
    () => [
      { key: 'date', title: 'Date', dataIndex: 'date' },
      {
        key: 'description',
        title: 'Description',
        dataIndex: 'description',
      },
      {
        key: 'accountId',
        title: 'Account',
        dataIndex: 'accountId',
        render: (text: string, record: any) =>
          record.txType === 'journal' ? (
            ''
          ) : (
            <div className="flex flex-col">
              <span>{business.accounts[text]?.name ?? 'Unknown'}</span>
              <span className="text-xs text-gray-500">
                {business.sites[record.siteId]?.name}
              </span>
            </div>
          ),
      },
      {
        key: 'otherSide',
        title: 'Category',
        dataIndex: 'otherSide',
        render: (text: string, record: any) => (
          <div className="flex flex-col">
            <span>
              {record.txType === 'journal' ? (
                <span className="text-primary">Journal</span>
              ) : record.category === 'transfer' ? (
                `${record.amount > 0 ? 'Transferred from' : 'Transferred to'} ${business.accounts[text.split(':')[0].split('-')[0]]?.name ?? 'Unknown'}`
              ) : (
                `${business.accounts[text.split(':')[0].split('-')[0]]?.name ?? 'Unknown'}${text.split(':').length > 1 ? ` +${text.split(':').length - 1}` : ''}`
              )}
            </span>
            <span className="text-xs text-gray-500">
              {business.sites[text.split(':')[0].split('-')[1]]?.name ?? ''}
            </span>
          </div>
        ),
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        align: 'end',
        render: (text: number, record: any) => (
          <span
            className={classNames({
              'text-green-600': record.txType !== 'journal' && text > 0,
              'text-primary': record.txType === 'journal',
            })}
          >
            {record.fcDetails?.receivedCcy ??
              business.accounts[record.accountId].currency}{' '}
            {Utils.formatPrice(Math.abs(text))}
          </span>
        ),
      },
      {
        key: 'actions',
        title: '',
        dataIndex: 'hk',
        render: (text: string, record: any) => (
          <Button type="link">
            <EditOutlined
              onClick={() =>
                record.txType === 'journal'
                  ? journalModal.current?.openJournal(
                      parseInt(text.split(':')[1], 10),
                    )
                  : txModal.current?.openTx(parseInt(text.split(':')[1], 10))
              }
            />
          </Button>
        ),
      },
    ],
    [business.accounts, business.sites, journalModal, txModal],
  );

  const accountOptions = useMemo(() => {
    return [
      {
        label: (
          <div className="w-full flex items-center justify-between gap-2">
            <span>All Accounts</span>
            <span>LKR {Utils.formatPrice(totalBalance)}</span>
          </div>
        ),
        value: '0',
      },
      ...Object.values(business.accountTypes)
        .map((at) => ({
          label: at.name,
          options: Object.values(business.accounts)
            .filter(
              (a) =>
                a.accountTypeId === at.accountTypeId &&
                a.activity &&
                (!a.siteId || a.siteId === siteId || !siteId || siteId === '0'),
            )
            .map((a) => ({
              label: (
                <div className="w-full flex items-center justify-between gap-2 max-w-full">
                  <span className="overflow-hidden text-ellipsis grow shrink">
                    {a.name}
                  </span>
                  <span>
                    {business.accounts[a.accountId]?.currency ??
                      business.currency}{' '}
                    {Utils.formatPrice(
                      (balances[a.accountId] ?? 0) *
                        (a.accountCategory === 'Liabilities' ? -1 : 1),
                    )}
                  </span>
                </div>
              ),
              value: a.accountId,
            })),
        }))
        .filter((a) => a.options.length > 0),
    ];
  }, [
    balances,
    business.accountTypes,
    business.accounts,
    business.currency,
    siteId,
    totalBalance,
  ]);

  useEffect(() => {
    setBalanceLoading(true);
    connection
      .get(`accounts/${business.businessId}/balances`, {
        params: {
          site: siteId,
          accounts: business
            .getAccountsInSite(siteId)
            .filter((a) => a.activity)
            .map((a) => a.accountId)
            .join(','),
        },
      })
      .then((res) => {
        setBalances(res.data);
      })
      .finally(() => setBalanceLoading(false));
  }, [accountId, business, connection, siteId]);

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>Transactions</WebUxTitle>
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 justify-between w-full">
          <Space>
            <Select
              className="w-[150px]"
              options={[
                { label: 'All sites', value: '0' },
                { label: 'Sites', options: business.select.sites },
              ]}
              value={siteId}
              onChange={(site) => {
                setSiteId(site);
                setAccountId('0');
              }}
            />
            <Select
              loading={balanceLoading}
              placement="bottomLeft"
              className="w-[250px]"
              popupClassName="min-w-[350px]"
              options={accountOptions}
              value={accountId}
              onChange={setAccountId}
            />
          </Space>
          <Space>
            <Button
              onClick={() =>
                txModal.current?.open(
                  'deposit',
                  siteId === '0' ? undefined : siteId,
                  accountId === '0' ? undefined : accountId,
                )
              }
            >
              Deposit
            </Button>
            <Button
              onClick={() =>
                txModal.current?.open(
                  'withdraw',
                  siteId === '0' ? undefined : siteId,
                  accountId === '0' ? undefined : accountId,
                )
              }
            >
              Withdraw
            </Button>
            <Button onClick={() => journalModal.current?.newJournal()}>
              Journal
            </Button>
          </Space>
        </div>
      </div>
      <Table
        size="small"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={transactions}
      />
      <div className="flex justify-center gap-2">
        <Button disabled={!lastKey} onClick={loadMore}>
          Load more transactions
        </Button>
      </div>
      <TransactionModal ref={txModal} />
      <JournalModal ref={journalModal} />
    </div>
  );
};
